import { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Checkbox from '@mui/material/Checkbox';
import Collapse from '@mui/material/Collapse';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Fade from '@mui/material/Fade';
import ListItemIcon from '@mui/material/ListItemIcon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Snackbar from '@mui/material/Snackbar';
import { alpha, useTheme } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import CreateNewFolderOutlinedIcon from '@mui/icons-material/CreateNewFolderOutlined';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FolderIcon from '@mui/icons-material/Folder';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import ReplayIcon from '@mui/icons-material/Replay';
import SlideshowOutlinedIcon from '@mui/icons-material/SlideshowOutlined';
import { DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useTranslation } from 'react-i18next';
import { API_BASE_URL, getAuthHeaders } from './auth';
import ChartCard, { ViewSwitch, chartFormatter } from './ChartCard';
import { AttachmentCard, InfoTooltip, modalHeaderBtnSx } from './chatMessageStyles';
import GraphGroupsBar from './GraphGroupsBar';
import { AddGraphsDialog, GroupPickerPopover } from './GraphGroupPicker';
import { moveItem } from './graphGroups';

/**
 * The "Graphs" tab of a data agent (docs/charts/PLAN.md, phases 4–5, and
 * docs/charts/README.md "Gruppen"): the charts the user starred in this
 * assistant's chat, newest first.
 *
 *  - each graph keeps how it is shown — Grafik / Tabelle / beides — as it was
 *    starred, switchable here; the deck builds its slide the same way
 *  - a click on a row opens it (the frozen spec, figures as starred)
 *  - "Erneut fragen" puts the graph's self-contained prompt into the chat
 *    input — that is how a graph gets today's figures
 *  - ticked graphs become a PowerPoint IN THE ORDER THEY WERE TICKED (the
 *    number on the checkbox), or are deleted in one go; "Alle auswählen" ticks
 *    the whole list (feedback 2026-10-12)
 *  - the moon / sun next to "PowerPoint erstellen" picks dark or light slides
 *    (remembered in this browser)
 *  - GROUPS ("Max Müller", "Management Meeting"): chips above the list; a
 *    graph can be in many groups (folder icon per row, or tick several →
 *    "Zu Gruppe"). Inside a group the graphs are dragged into order — that
 *    order is the deck's slide order — and the row's minus takes a graph out
 *    of the group only (with undo); deleting a graph is done under "Alle".
 *
 * The parent owns the list (it also needs it for the stars in the chat) and
 * the groups (useGraphGroups — without it the tab has no groups).
 *   graphs        [{id, title, prompt, spec, view, created_at}]
 *   groups        useGraphGroups(agentKey)
 *   onDelete      (graph) => Promise
 *   onDeleteMany  (ids) => Promise
 *   onViewChange  (graph, view) => void
 *   onAsk         (prompt) => void
 */

// Rows only move up and down.
const verticalOnly = ({ transform }) => ({ ...transform, x: 0 });

// Dark or light slides for the deck — a per-browser preference.
const DECK_THEME_KEY = 'graphs_deck_theme';
const readDeckTheme = () => {
  try { return localStorage.getItem(DECK_THEME_KEY) === 'light' ? 'light' : 'dark'; } catch (e) { return 'dark'; }
};

function GraphRow({ graph, order, showOrder, open, onToggleOpen, onToggle, onViewChange, onAsk, rowGroups,
  onOpenGroup, onGroups, inGroups, removeLabel, onRemove, drag, language }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const view = graph.view || 'chart';
  const fmt = chartFormatter(graph.spec || {});
  const asOf = graph.spec?.as_of ? fmt.date(graph.spec.as_of) : '';
  const ticked = order >= 0;
  const dragging = !!drag?.dragging;
  const tint = alpha(theme.palette.primary.main, 0.06);
  // background.paper is see-through in the dark themes; the dialog's own
  // surface is not — a lifted row must hide the rows it passes.
  const surface = theme.components?.MuiDialog?.styleOverrides?.paper?.background || theme.palette.background.default;

  return (
    <Box
      ref={drag?.ref}
      data-graph-row={graph.id}
      style={drag ? {
        transform: drag.transform,
        transition: [drag.transition, 'border-color 150ms', 'background-color 150ms', 'box-shadow 200ms']
          .filter(Boolean).join(', '),
      } : undefined}
      sx={{
        mb: 1, borderRadius: 1, border: 1, position: 'relative',
        borderColor: ticked || dragging ? alpha(theme.palette.primary.main, 0.6) : 'divider',
        bgcolor: ticked ? tint : 'rgba(255, 255, 255, 0.06)',
        transition: 'border-color 150ms, background-color 150ms, box-shadow 200ms',
        ...(dragging && {
          zIndex: 2,
          background: `linear-gradient(${tint}, ${tint}), linear-gradient(rgba(255, 255, 255, 0.06), rgba(255, 255, 255, 0.06)), ${surface}`,
          boxShadow: '0 14px 32px rgba(0, 0, 0, 0.45)',
        }),
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, p: 1, pr: 0.5, pl: drag ? 0.25 : 1, flexWrap: 'wrap' }}>
        {drag && (
          <InfoTooltip title={dragging ? '' : t('graphs_drag', { defaultValue: 'Ziehen, um die Reihenfolge zu ändern' })}>
            <Box
              component="span"
              ref={drag.handleRef}
              {...drag.handleProps}
              aria-label={t('graphs_drag', { defaultValue: 'Ziehen, um die Reihenfolge zu ändern' })}
              sx={{
                display: 'flex', alignItems: 'center', alignSelf: 'stretch', px: 0.25, borderRadius: 1,
                cursor: dragging ? 'grabbing' : 'grab', touchAction: 'none', color: dragging ? 'primary.main' : 'text.disabled',
                transition: 'color 150ms',
                '&:hover, &:focus-visible': { color: 'primary.main', outline: 'none' },
              }}
            >
              <DragIndicatorIcon sx={{ fontSize: 20 }} />
            </Box>
          </InfoTooltip>
        )}
        {/* Checkbox with the slide number once ticked */}
        <Box sx={{ position: 'relative', flexShrink: 0 }}>
          <Checkbox
            size="small"
            checked={ticked}
            onChange={onToggle}
            inputProps={{ 'aria-label': graph.title }}
          />
          {ticked && showOrder && (
            <Box sx={{
              position: 'absolute', top: 2, right: 0, minWidth: 14, height: 14, px: '3px',
              borderRadius: '7px', bgcolor: 'primary.main', color: 'primary.contrastText',
              fontSize: 10, fontWeight: 700, lineHeight: '14px', textAlign: 'center',
              pointerEvents: 'none',
            }}>
              {order + 1}
            </Box>
          )}
        </Box>
        <Box onClick={onToggleOpen} sx={{ flex: 1, minWidth: 180, cursor: 'pointer' }}>
          <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
            {graph.title}
          </Typography>
          <Typography variant="caption" sx={{
            color: 'text.secondary', display: '-webkit-box', WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical', overflow: 'hidden',
          }}>
            {graph.prompt}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block' }}>
            {graphDateText(graph, t, language, asOf)}
          </Typography>
          {rowGroups?.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
              {rowGroups.map(group => (
                <ButtonBase
                  key={group.id}
                  onClick={(e) => { e.stopPropagation(); onOpenGroup(group.id); }}
                  sx={{
                    display: 'inline-flex', alignItems: 'center', gap: 0.4, height: 20, px: 0.9, borderRadius: '10px',
                    fontSize: 11, fontWeight: 600, color: 'primary.main', bgcolor: alpha(theme.palette.primary.main, 0.1),
                    transition: 'background-color 150ms',
                    '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.2) },
                  }}
                >
                  <FolderOutlinedIcon sx={{ fontSize: 12 }} />
                  {group.name}
                </ButtonBase>
              ))}
            </Box>
          )}
        </Box>
        {/* How it is shown here and on its slide */}
        <ViewSwitch value={view} onChange={(next) => onViewChange(graph, next)} />
        <InfoTooltip title={open ? t('graphs_hide', { defaultValue: 'Grafik ausblenden' })
          : t('graphs_show', { defaultValue: 'Grafik anzeigen' })}>
          <IconButton size="small" onClick={onToggleOpen}
            sx={{ color: 'text.secondary', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }}>
            <ExpandMoreIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </InfoTooltip>
        <InfoTooltip title={t('graphs_ask_again', { defaultValue: 'Mit aktuellen Zahlen erneut fragen' })}>
          <IconButton size="small" onClick={() => onAsk(graph.prompt)}
            sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}>
            <ReplayIcon sx={{ fontSize: 17 }} />
          </IconButton>
        </InfoTooltip>
        {onGroups && (
          <InfoTooltip title={t('graphs_groups_of', { defaultValue: 'Gruppen zuordnen' })}>
            <IconButton size="small" onClick={onGroups}
              sx={{ color: inGroups ? 'primary.main' : 'text.secondary', '&:hover': { color: 'primary.main' } }}>
              {inGroups ? <FolderIcon sx={{ fontSize: 17 }} /> : <FolderOutlinedIcon sx={{ fontSize: 17 }} />}
            </IconButton>
          </InfoTooltip>
        )}
        <InfoTooltip title={removeLabel}>
          <IconButton size="small" onClick={onRemove}
            sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}>
            {drag ? <RemoveCircleOutlineIcon sx={{ fontSize: 17 }} /> : <DeleteOutlineIcon sx={{ fontSize: 17 }} />}
          </IconButton>
        </InfoTooltip>
      </Box>
      <Collapse in={open} unmountOnExit>
        <Box sx={{ px: 1, pb: 1 }}>
          <ChartCard spec={graph.spec} view={view} onViewChange={(next) => onViewChange(graph, next)} />
        </Box>
      </Collapse>
    </Box>
  );
}

/** A row inside a group: draggable by its handle; the others glide aside. */
function SortableGraphRow(props) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
    id: props.graph.id,
    transition: { duration: 220, easing: 'cubic-bezier(0.25, 1, 0.5, 1)' },
  });
  return (
    <GraphRow
      {...props}
      drag={{
        ref: setNodeRef,
        transform: CSS.Translate.toString(transform),
        transition,
        handleRef: setActivatorNodeRef,
        handleProps: { ...attributes, ...listeners },
        dragging: isDragging,
      }}
    />
  );
}

function graphDateText(graph, t, language, asOf) {
  const saved = (() => {
    if (!graph.created_at) return '';
    const d = new Date(graph.created_at);
    return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString(language === 'en' ? 'en-GB' : 'de-CH');
  })();
  return [asOf && t('chart_as_of', { defaultValue: 'Stand: {{date}}', date: asOf }),
    saved && t('graphs_saved_on', { defaultValue: 'gespeichert {{date}}', date: saved }),
  ].filter(Boolean).join(' · ');
}

function GraphsPanel({ graphs, agentKey, language, onDelete, onDeleteMany, onViewChange, onAsk, groups: groupsApi }) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState([]);          // ids, in tick order
  const [openId, setOpenId] = useState(null);
  const [deck, setDeck] = useState({ busy: false, card: null, error: false });
  // null | {graph} (one, from its trash icon) | {ids} (the ticked ones)
  const [confirm, setConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);
  // null | {anchor, graph} (its folder icon) | {anchor, bulk: true} ("Zu Gruppe")
  const [picker, setPicker] = useState(null);
  const [groupMenu, setGroupMenu] = useState(null);       // anchor of the open group's ⋮
  const [renamingId, setRenamingId] = useState(null);
  const [renameNext, setRenameNext] = useState(null);     // rename picked in ⋮, starts when it closed
  const [confirmGroup, setConfirmGroup] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [toast, setToast] = useState(null);               // {key, message, action?: {label, run}}
  const [deckTheme, setDeckTheme] = useState(readDeckTheme); // 'dark' | 'light' slides

  const toggleDeckTheme = () => {
    const next = deckTheme === 'light' ? 'dark' : 'light';
    setDeckTheme(next);
    try { localStorage.setItem(DECK_THEME_KEY, next); } catch (e) { /* private mode: not remembered */ }
    // The card on screen holds slides of the other theme: build the same deck
    // again, so "Präsentation öffnen" always opens what the switch shows.
    if (deck.card && deck.request) buildDeck({ ...deck.request, theme: next });
  };
  const deckThemeLabel = deckTheme === 'light'
    ? t('graphs_deck_light', { defaultValue: 'Helle Folien · klicken für dunkle' })
    : t('graphs_deck_dark', { defaultValue: 'Dunkle Folien · klicken für helle' });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const withGroups = !!groupsApi;
  const byId = useMemo(() => new Map(graphs.map(g => [g.id, g])), [graphs]);
  // Links to a graph deleted meanwhile (the star in the chat) are not shown.
  const rawGroups = groupsApi?.groups;
  const groups = useMemo(() => (rawGroups || []).map((g) => {
    const ids = g.graph_ids.filter(id => byId.has(id));
    return { ...g, graph_ids: ids, count: ids.length };
  }), [rawGroups, byId]);
  const activeGroup = (withGroups && groups.find(g => g.id === groupsApi.activeId)) || null;
  const shown = activeGroup ? activeGroup.graph_ids.map(id => byId.get(id)) : graphs;
  const groupsOf = (id) => groups.filter(g => g.graph_ids.includes(id));

  // Another view starts without ticks and without the last deck.
  const viewKey = activeGroup ? activeGroup.id : 'all';
  useEffect(() => {
    setSelected([]);
    setDeck({ busy: false, card: null, error: false });
  }, [viewKey]);

  const visibleSelection = selected.filter(id => shown.some(g => g.id === id));
  const allSelected = shown.length > 0 && visibleSelection.length === shown.length;

  const toggle = (id) => setSelected(s => (s.includes(id) ? s.filter(x => x !== id) : [...s, id]));
  // Ticking all keeps what was already ticked first, the rest in list order.
  const toggleAll = () => setSelected(allSelected ? []
    : [...visibleSelection, ...shown.map(g => g.id).filter(id => !visibleSelection.includes(id))]);

  // In a group the deck follows the group's order: the ticked graphs, or all.
  const deckIds = activeGroup
    ? shown.map(g => g.id).filter(id => !visibleSelection.length || visibleSelection.includes(id))
    : visibleSelection;

  const notify = (message, action) => setToast({ key: Date.now(), message, action });
  const failed = () => notify(t('graphs_group_error', { defaultValue: 'Das hat nicht geklappt. Bitte nochmal versuchen.' }));

  /** Build a deck. Its `request` (graphs, group, theme) stays with the card,
   *  so switching dark/light can build the SAME deck again; the card says
   *  which slides it holds. */
  const buildDeck = async (request) => {
    setDeck({ busy: true, card: null, error: false, request: null });
    try {
      const res = await fetch(`${API_BASE_URL}/api/graphs/deck`, {
        method: 'POST',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          agent: agentKey, ids: request.ids, language: language || 'de', theme: request.theme,
          ...(request.groupId ? { group_id: request.groupId } : {}),
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const card = await res.json();
      const slides = request.theme === 'light'
        ? t('graphs_deck_theme_light', { defaultValue: 'helle Folien' })
        : t('graphs_deck_theme_dark', { defaultValue: 'dunkle Folien' });
      setDeck({ busy: false, card: { ...card, subtitle: [card.subtitle, slides].filter(Boolean).join(' · ') },
        error: false, request });
    } catch (e) {
      console.error('Graph deck failed:', e);
      setDeck({ busy: false, card: null, error: true, request: null });
    }
  };

  const createDeck = () => {
    if (deckIds.length) buildDeck({ ids: deckIds, theme: deckTheme, groupId: activeGroup ? activeGroup.id : null });
  };

  const confirmDelete = async () => {
    if (!confirm) return;
    setDeleting(true);
    try {
      const ids = confirm.ids || [confirm.graph.id];
      if (confirm.ids) await onDeleteMany(ids);
      else await onDelete(confirm.graph);
      setSelected(s => s.filter(id => !ids.includes(id)));
      setConfirm(null);
    } catch (e) {
      console.error('Failed to delete graphs:', e);
    } finally {
      setDeleting(false);
    }
  };

  // ── Groups ──
  const selectGroup = (id) => {
    setRenamingId(null);
    groupsApi.setActiveId(id);
  };

  const createGroup = async (name) => {
    const group = await groupsApi.create(name);
    groupsApi.setActiveId(group.id);
  };

  /** Folder icon of one graph: tick = in the group, untick = out. */
  const toggleGraphIn = (graph) => (group) => {
    const run = group.graph_ids.includes(graph.id)
      ? groupsApi.removeGraphs(group.id, [graph.id])
      : groupsApi.add(group.id, [graph.id]);
    run.catch(failed);
  };

  const openAction = (group) => ({ label: t('graphs_open_group', { defaultValue: 'Öffnen' }), run: () => selectGroup(group.id) });

  /** "Zu Gruppe": the ticked graphs, in tick order, to the end of a group. */
  const addSelectionTo = (group) => {
    groupsApi.add(group.id, visibleSelection).catch(failed);
    notify(t('graphs_added_to_group', { defaultValue: 'Zu „{{name}}“ hinzugefügt', name: group.name }), openAction(group));
  };

  const createWithSelection = async (name) => {
    const group = await groupsApi.create(name, visibleSelection);
    notify(t('graphs_group_created', { defaultValue: 'Gruppe „{{name}}“ erstellt', name: group.name }), openAction(group));
  };

  /** Out of the open group (the graphs stay) — undo puts them back in place. */
  const removeFromGroup = (ids) => {
    const group = activeGroup;
    const before = group.graph_ids;
    groupsApi.removeGraphs(group.id, ids).catch(failed);
    setSelected(s => s.filter(id => !ids.includes(id)));
    notify(t('graphs_removed_from_group', { defaultValue: 'Aus „{{name}}“ entfernt', name: group.name }), {
      label: t('graphs_undo', { defaultValue: 'Rückgängig' }),
      run: () => {
        groupsApi.add(group.id, ids).catch(failed);
        groupsApi.reorder(group.id, before).catch(failed);
      },
    });
  };

  const addToGroup = (ids) => {
    setAddOpen(false);
    groupsApi.add(activeGroup.id, ids).catch(failed);
  };

  const confirmDeleteGroup = () => {
    const group = confirmGroup;
    setConfirmGroup(null);
    groupsApi.remove(group.id).catch(failed);
  };

  // ── Drag & drop inside a group ──
  const onDragStart = () => { document.body.style.cursor = 'grabbing'; };
  const onDragEnd = ({ active, over }) => {
    document.body.style.cursor = '';
    if (!activeGroup || !over || active.id === over.id) return;
    const ids = activeGroup.graph_ids;
    groupsApi.reorder(activeGroup.id, moveItem(ids, ids.indexOf(active.id), ids.indexOf(over.id))).catch(failed);
  };
  const onDragCancel = () => { document.body.style.cursor = ''; };

  const barBtnSx = { ...modalHeaderBtnSx, px: 1.5, py: 0.5, fontSize: '13px', fontWeight: 600 };

  const rowProps = (graph) => {
    const rowGroups = groupsOf(graph.id);
    return {
      key: graph.id,
      graph,
      language,
      order: visibleSelection.indexOf(graph.id),
      showOrder: !activeGroup,
      open: openId === graph.id,
      onToggleOpen: () => setOpenId(openId === graph.id ? null : graph.id),
      onToggle: () => toggle(graph.id),
      onViewChange,
      onAsk,
      rowGroups: activeGroup ? [] : rowGroups,
      onOpenGroup: selectGroup,
      inGroups: rowGroups.length > 0,
      onGroups: withGroups ? (e) => setPicker({ anchor: e.currentTarget, graph }) : null,
      removeLabel: activeGroup
        ? t('graphs_remove_from_group', { defaultValue: 'Aus Gruppe entfernen' })
        : t('graphs_delete', { defaultValue: 'Grafik löschen' }),
      onRemove: activeGroup ? () => removeFromGroup([graph.id]) : () => setConfirm({ graph }),
    };
  };

  const pickerGraph = picker?.graph && byId.get(picker.graph.id);

  return (
    <Box sx={{ pt: 2, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      {/* ─── Groups ─── */}
      {withGroups && (graphs.length > 0 || groups.length > 0) && (
        <GraphGroupsBar
          groups={groups}
          total={graphs.length}
          activeId={activeGroup ? activeGroup.id : null}
          onSelect={selectGroup}
          onCreate={createGroup}
          renamingId={renamingId}
          onRename={groupsApi.rename}
          onRenameEnd={() => setRenamingId(null)}
        />
      )}

      {/* ─── Action bar: select all, deck, groups and delete from the ticked ones ─── */}
      {(shown.length > 0 || activeGroup) && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
          {shown.length > 0 && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', pl: 1 }}>
                <Checkbox
                  size="small"
                  checked={allSelected}
                  indeterminate={visibleSelection.length > 0 && !allSelected}
                  onChange={toggleAll}
                  inputProps={{ 'aria-label': t('graphs_select_all', { defaultValue: 'Alle auswählen' }) }}
                />
                <Typography variant="body2" onClick={toggleAll} sx={{ color: 'text.secondary', cursor: 'pointer', mr: 1 }}>
                  {visibleSelection.length
                    ? t('graphs_selected', { defaultValue: '{{count}} ausgewählt', count: visibleSelection.length })
                    : t('graphs_select_all', { defaultValue: 'Alle auswählen' })}
                </Typography>
              </Box>
              <Button
                onClick={createDeck}
                disabled={!deckIds.length || deck.busy}
                size="small"
                startIcon={deck.busy ? <CircularProgress size={14} /> : <SlideshowOutlinedIcon />}
                sx={barBtnSx}
              >
                {activeGroup && !visibleSelection.length
                  ? t('graphs_group_deck', { defaultValue: 'PowerPoint der Gruppe' })
                  : t('graphs_create_deck', { defaultValue: 'PowerPoint erstellen' })}
              </Button>
              {/* Dark or light slides — moon / sun, remembered in this browser */}
              {/* (span: the tooltip still works while the button is disabled) */}
              <InfoTooltip title={deckThemeLabel}>
                <Box component="span" sx={{ display: 'inline-flex', ml: -0.5 }}>
                  <IconButton
                    size="small"
                    onClick={toggleDeckTheme}
                    disabled={deck.busy}
                    aria-label={deckThemeLabel}
                    sx={{ ...modalHeaderBtnSx, p: 0.6 }}
                  >
                    <Box
                      key={deckTheme}
                      component="span"
                      sx={{
                        display: 'flex',
                        animation: 'deckThemeIn 260ms cubic-bezier(0.25, 1, 0.5, 1)',
                        '@keyframes deckThemeIn': {
                          from: { opacity: 0, transform: 'rotate(-90deg) scale(0.6)' },
                          to: { opacity: 1, transform: 'none' },
                        },
                      }}
                    >
                      {deckTheme === 'light'
                        ? <LightModeOutlinedIcon sx={{ fontSize: 18 }} />
                        : <DarkModeOutlinedIcon sx={{ fontSize: 18 }} />}
                    </Box>
                  </IconButton>
                </Box>
              </InfoTooltip>
            </>
          )}
          {activeGroup ? (
            <>
              {shown.length > 0 && (
                <>
                  <Button onClick={() => setAddOpen(true)} size="small" startIcon={<AddIcon />} sx={barBtnSx}>
                    {t('graphs_group_add_graphs', { defaultValue: 'Grafiken hinzufügen' })}
                  </Button>
                  <Button
                    onClick={() => removeFromGroup(visibleSelection)}
                    disabled={!visibleSelection.length}
                    size="small"
                    startIcon={<RemoveCircleOutlineIcon />}
                    sx={barBtnSx}
                  >
                    {t('graphs_remove_from_group', { defaultValue: 'Aus Gruppe entfernen' })}
                  </Button>
                </>
              )}
              <Box sx={{ flex: 1 }} />
              <InfoTooltip title={t('graphs_group_menu', { defaultValue: 'Gruppe bearbeiten' })}>
                <IconButton
                  size="small"
                  aria-label={t('graphs_group_menu', { defaultValue: 'Gruppe bearbeiten' })}
                  onClick={(e) => setGroupMenu(e.currentTarget)}
                  sx={{ ...modalHeaderBtnSx, p: 0.6 }}
                >
                  <MoreVertIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </InfoTooltip>
            </>
          ) : (
            <>
              {withGroups && (
                <Button
                  onClick={(e) => setPicker({ anchor: e.currentTarget, bulk: true })}
                  disabled={!visibleSelection.length}
                  size="small"
                  startIcon={<CreateNewFolderOutlinedIcon />}
                  sx={barBtnSx}
                >
                  {t('graphs_add_to_group', { defaultValue: 'Zu Gruppe' })}
                </Button>
              )}
              <Button
                onClick={() => setConfirm({ ids: visibleSelection })}
                disabled={!visibleSelection.length}
                size="small"
                startIcon={<DeleteOutlineIcon />}
                sx={{ ...barBtnSx, '&:hover': { color: 'error.main', borderColor: 'error.main' } }}
              >
                {t('graphs_delete_selected', { defaultValue: 'Ausgewählte löschen' })}
              </Button>
            </>
          )}
        </Box>
      )}

      {deck.card && (
        <Box sx={{ mb: 1.5 }}>
          <AttachmentCard attachment={deck.card} openLabel={t('efd_attachment_open')} />
        </Box>
      )}
      {deck.error && (
        <Typography variant="body2" sx={{ color: 'error.main', mb: 1.5 }}>
          {t('graphs_deck_error', { defaultValue: 'Die Präsentation konnte nicht erstellt werden.' })}
        </Typography>
      )}

      <Fade in key={viewKey} timeout={200}>
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          {graphs.length === 0 ? (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {t('graphs_empty', { defaultValue: 'Noch keine Grafiken gespeichert. Markiere eine Grafik im Chat mit dem Stern.' })}
            </Typography>
          ) : activeGroup && shown.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 5, px: 2 }}>
              <FolderOutlinedIcon sx={{ fontSize: 36, color: 'text.disabled', mb: 1 }} />
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {t('graphs_group_empty', { defaultValue: 'Diese Gruppe ist noch leer.' })}
              </Typography>
              <Button onClick={() => setAddOpen(true)} size="small" startIcon={<AddIcon />} sx={{ ...barBtnSx, mt: 2 }}>
                {t('graphs_group_add_graphs', { defaultValue: 'Grafiken hinzufügen' })}
              </Button>
            </Box>
          ) : activeGroup ? (
            <>
              {shown.length > 1 && (
                <Typography variant="caption" sx={{ color: 'text.disabled', mb: 1, pl: 0.5 }}>
                  {t('graphs_group_hint', { defaultValue: 'Ziehe die Grafiken in die Reihenfolge der Präsentation.' })}
                </Typography>
              )}
              <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', pr: 0.5 }}>
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  modifiers={[verticalOnly]}
                  onDragStart={onDragStart}
                  onDragEnd={onDragEnd}
                  onDragCancel={onDragCancel}
                >
                  <SortableContext items={activeGroup.graph_ids} strategy={verticalListSortingStrategy}>
                    {shown.map(graph => {
                      const { key, ...props } = rowProps(graph);
                      return <SortableGraphRow key={key} {...props} />;
                    })}
                  </SortableContext>
                </DndContext>
              </Box>
            </>
          ) : (
            <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', pr: 0.5 }}>
              {shown.map(graph => {
                const { key, ...props } = rowProps(graph);
                return <GraphRow key={key} {...props} />;
              })}
            </Box>
          )}
        </Box>
      </Fade>

      {/* ─── Group pickers, menus and dialogs ─── */}
      {withGroups && (
        <>
          <GroupPickerPopover
            anchorEl={pickerGraph || picker?.bulk ? picker.anchor : null}
            onClose={() => setPicker(null)}
            title={picker?.bulk
              ? t('graphs_add_to_group_title', { defaultValue: 'Ausgewählte zu Gruppe hinzufügen ({{count}})', count: visibleSelection.length })
              : t('graphs_groups_of', { defaultValue: 'Gruppen zuordnen' })}
            groups={groups}
            checked={(group) => {
              if (!picker?.bulk) return !!pickerGraph && group.graph_ids.includes(pickerGraph.id);
              const inIt = visibleSelection.filter(id => group.graph_ids.includes(id)).length;
              return inIt && inIt === visibleSelection.length ? true : inIt ? 'some' : false;
            }}
            onPick={picker?.bulk ? addSelectionTo : (pickerGraph ? toggleGraphIn(pickerGraph) : () => {})}
            onCreate={picker?.bulk ? createWithSelection : (name) => groupsApi.create(name, [pickerGraph.id])}
            closeOnPick={!!picker?.bulk}
          />
          <AddGraphsDialog
            open={addOpen && !!activeGroup}
            group={activeGroup}
            graphs={graphs}
            onClose={() => setAddOpen(false)}
            onAdd={addToGroup}
            dateText={(graph) => graphDateText(graph, t, language,
              graph.spec?.as_of ? chartFormatter(graph.spec).date(graph.spec.as_of) : '')}
          />
          <Menu
            anchorEl={groupMenu}
            open={!!groupMenu}
            // The rename field takes the focus: it opens once the menu is
            // gone (its focus trap would pull the focus back while it fades),
            // and the menu does not restore the focus to ⋮ afterwards.
            disableRestoreFocus
            TransitionProps={{ onExited: () => { if (renameNext) setRenamingId(renameNext); setRenameNext(null); } }}
            onClose={() => setGroupMenu(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem onClick={() => { setRenameNext(activeGroup?.id ?? null); setGroupMenu(null); }}>
              <ListItemIcon><EditOutlinedIcon fontSize="small" /></ListItemIcon>
              {t('graphs_group_rename', { defaultValue: 'Umbenennen' })}
            </MenuItem>
            <MenuItem onClick={() => { setGroupMenu(null); setConfirmGroup(activeGroup); }} sx={{ color: 'error.main' }}>
              <ListItemIcon><DeleteOutlineIcon fontSize="small" sx={{ color: 'error.main' }} /></ListItemIcon>
              {t('graphs_group_delete', { defaultValue: 'Gruppe löschen' })}
            </MenuItem>
          </Menu>
          <Dialog open={!!confirmGroup} onClose={() => setConfirmGroup(null)} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ fontSize: '16px', fontWeight: 600 }}>
              {t('graphs_group_delete_title', { defaultValue: 'Gruppe „{{name}}“ löschen?', name: confirmGroup?.name || '' })}
            </DialogTitle>
            <DialogContent>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {t('graphs_group_delete_text', { defaultValue: 'Die Grafiken bleiben unter „Alle“ gespeichert.' })}
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setConfirmGroup(null)}>{t('cancel', { defaultValue: 'Abbrechen' })}</Button>
              <Button onClick={confirmDeleteGroup} color="error" variant="contained">
                {t('graphs_group_delete', { defaultValue: 'Gruppe löschen' })}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}

      <Dialog open={!!confirm} onClose={() => !deleting && setConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: '16px', fontWeight: 600 }}>
          {confirm?.ids && confirm.ids.length > 1
            ? t('graphs_delete_many_title', { defaultValue: '{{count}} Grafiken löschen?', count: confirm.ids.length })
            : t('graphs_delete_title', { defaultValue: 'Grafik löschen?' })}
        </DialogTitle>
        <DialogContent>
          <Box component="ul" sx={{ m: 0, pl: 2.5, color: 'text.secondary', maxHeight: 220, overflowY: 'auto' }}>
            {(confirm?.ids ? graphs.filter(g => confirm.ids.includes(g.id)) : confirm ? [confirm.graph] : [])
              .map((g) => {
                const names = groupsOf(g.id).map(x => x.name);
                return (
                  <Typography key={g.id} component="li" variant="body2">
                    {g.title}
                    {names.length > 0 && (
                      <Typography component="span" variant="caption" sx={{ color: 'text.disabled', display: 'block' }}>
                        {t('graphs_in_groups', { defaultValue: 'in Gruppen: {{names}}', names: names.join(', ') })}
                      </Typography>
                    )}
                  </Typography>
                );
              })}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)} disabled={deleting}>
            {t('cancel', { defaultValue: 'Abbrechen' })}
          </Button>
          <Button onClick={confirmDelete} color="error" variant="contained" disabled={deleting}>
            {deleting ? <CircularProgress size={18} /> : t('delete', { defaultValue: 'Löschen' })}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        key={toast?.key}
        open={!!toast}
        autoHideDuration={5000}
        onClose={(_, reason) => { if (reason !== 'clickaway') setToast(null); }}
        message={toast?.message || ''}
        action={toast?.action ? (
          <Button size="small" onClick={() => { toast.action.run(); setToast(null); }}
            sx={{ color: 'primary.main', fontWeight: 600 }}>
            {toast.action.label}
          </Button>
        ) : null}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}

export default GraphsPanel;
